import { useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import { persistedOverviewAtom, initOverviewDataAtom } from '../atoms';
import { getPersistence } from '@src/api/persistence';
import { OVERVIEW_PERSISTENCE_KEY } from '../constants';

// Global state to track if initial persistence has been loaded
let persistenceLoaded = false;
let persistencePromise: Promise<any> | null = null;

// Blocking persistence loader - called once on dashboard mount
const loadPersistenceData = async (): Promise<{
  configuration: any;
  isFromOverviewDefault: boolean;
}> => {
  if (persistenceLoaded && persistencePromise) {
    return persistencePromise;
  }

  if (!persistencePromise) {
    persistencePromise = (async () => {
      try {
        const { data } = await getPersistence({ key: OVERVIEW_PERSISTENCE_KEY });
        return {
          configuration: data?.configuration || null,
          isFromOverviewDefault: data?.isFromCustomerDefault || false,
        };
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return {
            configuration: null,
            isFromOverviewDefault: false,
          };
        }
        throw error;
      }
    })();
  }

  return persistencePromise;
};

/**
 * Blocking persistence hook that prevents component rendering until persistence data is loaded.
 * This should be used at the dashboard level to ensure all components render with correct data.
 */
export const useBlockingPersistence = () => {
  const [persistedOverview, setPersistedOverview] = useAtom(persistedOverviewAtom);
  const [, initOverview] = useAtom(initOverviewDataAtom);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isFromOverviewDefault, setIsFromOverviewDefault] = useState(false);

  useEffect(() => {
    const initializePersistence = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const { configuration, isFromOverviewDefault: isFromDefault } = await loadPersistenceData();

        if (configuration) {
          setPersistedOverview(configuration);
          setIsFromOverviewDefault(isFromDefault);
        } else {
          initOverview();
        }

        persistenceLoaded = true;
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading persistence data:', err);
        setError(err as Error);

        initOverview();
        persistenceLoaded = true;
        setIsLoading(false);
      }
    };

    initializePersistence();

    return () => {
      persistenceLoaded = false;
      persistencePromise = null;
    };
  }, [setPersistedOverview, initOverview]);

  return {
    isLoading,
    error,
    isFromOverviewDefault,
  };
};
